import PasswordInput from '@atoms/PasswordInput';
import { ButtonPrimaryLarge } from '@styled';
import React, { useContext, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useStytch } from '@stytch/react';
import { PasswordChangeContext } from '@context/PasswordChangeProvider';

const NewPassword = ({ withCurrent, email, onUpdate, buttonText }) => {
    const stytch = useStytch();

    const {
        currentPassword,
        setCurrentPassword,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        currentPasswordError,
        newPasswordError,
        setNewPasswordError,
        passwordScore,
        setPasswordScore,
    } = useContext(PasswordChangeContext);

    let confirmPasswordError = null;

    if (confirmPassword.length === 0 || newPassword === confirmPassword) {
        confirmPasswordError = null;
    } else {
        confirmPasswordError = 'Passwords do not match.';
    }

    useEffect(() => {
        if (newPassword.length >= 3) {
            const data = { password: newPassword };

            // “If the email address is included, it will be factored into strength evaluation via our
            // password breach checks. If you do not include the email, it is possible that the strength
            // check response will evaluate as valid – but the password will fail with a weak_password
            // error when used in the Create password endpoint due to a breach check failure.”
            //
            // https://stytch.com/docs/sdks/javascript-sdk/passwords
            if (email) {
                data.email = email;
            }

            stytch.passwords.strengthCheck(data).then(resp => {
                const {
                    breached_password: breachedPassword,
                    valid_password: validPassword,
                    score,
                    feedback: { suggestions: feedbackSuggestions },
                } = resp;

                setPasswordScore(0);

                if (!validPassword && !breachedPassword) {
                    setNewPasswordError(feedbackSuggestions.join(' ') || 'Please choose a stronger password.');

                    return;
                }

                if (breachedPassword) {
                    setNewPasswordError(
                        'The password you have chosen is not considered secure. Please choose a different password.'
                    );

                    return;
                }

                setPasswordScore(score);
                setNewPasswordError(null);
            });
        } else {
            setPasswordScore(0);
        }
    }, [newPassword, email, stytch, setPasswordScore, setNewPasswordError]);

    const canContinue = () =>
        passwordScore >= 3 &&
        newPassword.length > 0 &&
        newPasswordError === null &&
        confirmPassword.length > 0 &&
        !confirmPasswordError &&
        (!withCurrent || (currentPassword.length > 0 && !currentPasswordError));

    return (
        <>
            <p className="display8 mb-4">
                Passwords should be at least 12 characters and cannot contain your name or your company name.
            </p>
            <form
                onSubmit={e => {
                    e.preventDefault();
                    onUpdate({ currentPassword, newPassword, confirmPassword });
                }}
            >
                {withCurrent && (
                    <PasswordInput
                        label="Current Password"
                        required
                        error={currentPasswordError}
                        onChange={e => setCurrentPassword(e.target.value)}
                    />
                )}

                <PasswordInput
                    label="New Password"
                    required
                    error={newPasswordError}
                    onChange={e => setNewPassword(e.target.value)}
                    score={passwordScore}
                />

                <PasswordInput
                    label="Confirm New Password"
                    error={confirmPasswordError}
                    required
                    onChange={e => setConfirmPassword(e.target.value)}
                />

                <ButtonPrimaryLarge className="w-100 mt-1" disabled={!canContinue()}>
                    {buttonText ?? 'Continue'}
                </ButtonPrimaryLarge>
            </form>
        </>
    );
};

NewPassword.props = {
    withCurrent: PropTypes.boolean,
    email: PropTypes.string,
    onUpdate: PropTypes.function,
    buttonText: PropTypes.string,
};

export default NewPassword;
